import { useEffect, useRef } from 'react'
import { DASHBOARD_TOUR_AUTO_START } from '@/lib/onboardingUiFlags'
import '@/styles/shepherd-custom.css'
import linaAvatar from '@assets/lina desk_1764231656647.png'

const TOUR_STORAGE_KEY = 'lina_onboarding_tour_completed'

interface DashboardTourProps {
  isFirstTimeUser: boolean
  onboardingProgress?: number
  onTourComplete?: () => void
  manualTrigger?: boolean
  onTourStart?: () => void
  videoShowing?: boolean
}

function createStepContent(text: string): string {
  return `
    <div class="lina-tour-content">
      <img src="${linaAvatar}" alt="Lina" class="lina-tour-avatar" />
      <div class="lina-tour-text">${text}</div>
    </div>
  `
}

export function DashboardTour({
  isFirstTimeUser,
  onTourComplete,
  manualTrigger,
  onTourStart,
  videoShowing = false
}: DashboardTourProps) {
  const tourRef = useRef<any>(null)
  const tourActiveRef = useRef(false)

  useEffect(() => {
    let shouldStartTour = false

    // Only run tour on desktop/tablet (>= 1024px)
    if (window.innerWidth < 1024 && !manualTrigger) {
      return
    }

    if (manualTrigger) {
      shouldStartTour = true
    } else {
      if (!DASHBOARD_TOUR_AUTO_START) return // auto-start off for all customers (onboardingUiFlags)
      if (videoShowing) return

      let tourCompleted: string | null = null
      try {
        tourCompleted = localStorage.getItem(TOUR_STORAGE_KEY)
      } catch {
        return
      }
      if (tourCompleted === 'true') return

      // Auto-start only on the new-user page: the steps explain the how-to cards, which the
      // returning-user Cockpit no longer renders (their anchors would be missing). The
      // sidebar's manual trigger still works everywhere.
      if (!isFirstTimeUser) return
      shouldStartTour = true
    }

    if (shouldStartTour && tourRef.current) {
      if (tourActiveRef.current) {
        tourRef.current.cancel()
      }
      tourRef.current = null
      tourActiveRef.current = false
    }

    const initTour = async () => {
      const ShepherdModule = await import('shepherd.js')
      await import('shepherd.js/dist/css/shepherd.css')
      const Shepherd = ShepherdModule.default

      const tour = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
          cancelIcon: { enabled: true },
          scrollTo: { behavior: 'smooth', block: 'center' },
          modalOverlayOpeningPadding: 8,
          modalOverlayOpeningRadius: 12,
        },
        exitOnEsc: true,
        keyboardNavigation: true,
      })

      // Step 1: Begrüßung
      tour.addStep({
        id: 'welcome',
        text: createStepContent(
          `Hallo! Ich bin Lina, Ihre Marketing-Strategin. Die anderen im Team, Max, Emma und Alex, lernen Sie noch kennen. Aber zuerst zeige ich Ihnen, wie alles funktioniert.`
        ),
        // Centered: the old welcome card no longer exists (Cockpit redesign), a dead anchor
        // made Shepherd log an error on every start.
        buttons: [
          { text: 'Überspringen', action: tour.cancel, classes: 'shepherd-button-secondary' },
          { text: 'Los geht\'s →', action: tour.next, classes: 'shepherd-button-primary' }
        ]
      })

      // Step 2: Die 4 Säulen - Überblick
      tour.addStep({
        id: 'four-pillars',
        text: createStepContent(
          `Das hier ist Ihre Kommandozentrale. Vier Wege zu gutem Content, je nachdem, was Sie gerade brauchen.`
        ),
        attachTo: { element: '[data-tour="four-pillars"]', on: 'top' },
        buttons: [
          { text: '← Zurück', action: tour.back, classes: 'shepherd-button-secondary' },
          { text: 'Weiter →', action: tour.next, classes: 'shepherd-button-primary' }
        ]
      })

      // Step 3: Foto → Content
      tour.addStep({
        id: 'photo-card',
        text: createStepContent(
          `Mein Favorit für den schnellen Start: Foto hochladen, und Max schreibt Ihnen Beiträge für jede Plattform. Einfacher geht es nicht.`
        ),
        attachTo: { element: '[data-testid="card-hero-photo-to-content"]', on: 'right' },
        buttons: [
          { text: '← Zurück', action: tour.back, classes: 'shepherd-button-secondary' },
          { text: 'Weiter →', action: tour.next, classes: 'shepherd-button-primary' }
        ]
      })

      // Step 4: URL → Content
      tour.addStep({
        id: 'url-card',
        text: createStepContent(
          `Gute Ideen sind überall. YouTube, Blogs, Artikel. Geben Sie mir einen Link, und ich helfe Ihnen, daraus Content zu machen, der nach Ihnen klingt und nicht nach Copy-Paste.`
        ),
        attachTo: { element: '[data-testid="card-hero-repurposing-content"]', on: 'left' },
        buttons: [
          { text: '← Zurück', action: tour.back, classes: 'shepherd-button-secondary' },
          { text: 'Weiter →', action: tour.next, classes: 'shepherd-button-primary' }
        ]
      })

      // Step 5: Visual Brand Creator
      tour.addStep({
        id: 'visual-brand',
        text: createStepContent(
          `Emma braucht nur ein Foto. Geben Sie ihr optional ein Stil-Referenzbild, und sie trifft Ihren Look. Ergebnis: mehrere Visuals plus fertige Werbetexte.`
        ),
        attachTo: { element: '[data-testid="card-hero-visual-brand-creator"]', on: 'right' },
        buttons: [
          { text: '← Zurück', action: tour.back, classes: 'shepherd-button-secondary' },
          { text: 'Weiter →', action: tour.next, classes: 'shepherd-button-primary' }
        ]
      })

      // Step 6: Frag Lina
      tour.addStep({
        id: 'lina-chat',
        text: createStepContent(
          `Und hier oben bin ich direkt für Sie da. Fragen Sie mich etwas zu Ihrem Marketing, ich kenne Ihren Account.`
        ),
        attachTo: { element: '[data-testid="card-lina-briefing"]', on: 'left' },
        buttons: [
          { text: '← Zurück', action: tour.back, classes: 'shepherd-button-secondary' },
          { text: 'Weiter →', action: tour.next, classes: 'shepherd-button-primary' }
        ]
      })

      // (former Step 7 "Marketing Tools" removed: it anchored to the deleted
      // ModuleCarousel and described modules that no longer exist)

      // Step 8: Sidebar - Content & Setup
      tour.addStep({
        id: 'sidebar-overview',
        text: createStepContent(
          `Die Seitenleiste ist Ihr Cockpit. Oben 'Content': Dort entsteht Ihr Content. Branchen-News täglich, Beiträge nach Themen jeden Montag, alles automatisch. Unten 'Setup': Dort stellen Sie die Weichen. Strategie, Briefing, Plattformen.`
        ),
        attachTo: { element: '[data-section="content-management"]', on: 'right' },
        buttons: [
          { text: '← Zurück', action: tour.back, classes: 'shepherd-button-secondary' },
          { text: 'Weiter →', action: tour.next, classes: 'shepherd-button-primary' }
        ]
      })

      // Step 9: Plattform verbinden
      tour.addStep({
        id: 'platform-connections',
        text: createStepContent(
          `Kurzer Zwischenstopp: Verknüpfen Sie hier Ihre Kanäle, damit wir auch posten können. LinkedIn, Facebook, Instagram. Dauert zwei Minuten, hält lange.`
        ),
        attachTo: { element: '[data-testid="sidebar-plattform-verbinden"]', on: 'right' },
        buttons: [
          { text: '← Zurück', action: tour.back, classes: 'shepherd-button-secondary' },
          { text: 'Weiter →', action: tour.next, classes: 'shepherd-button-primary' }
        ]
      })

      // Step 10: Marketingstrategie
      tour.addStep({
        id: 'marketing-strategy',
        text: createStepContent(
          `Hier wird es richtig gut. Sie beantworten ein paar Fragen zu Ihrem Hotel, den Rest erstellt die KI: Brand Story, Positionierung, Kern-Botschaften, Content-Säulen. Das komplette Fundament.<br><br>Alles lässt sich anpassen und verfeinern. Einmal aufgesetzt, bleibt Ihre Strategie stehen, und die Beiträge nach Themen bauen jeden Montag automatisch darauf auf. Übrigens: In der Einführungsphase ist das für alle offen.`
        ),
        attachTo: { element: '[data-testid="sidebar-marketingstrategie"]', on: 'right' },
        buttons: [
          { text: '← Zurück', action: tour.back, classes: 'shepherd-button-secondary' },
          { text: 'Weiter →', action: tour.next, classes: 'shepherd-button-primary' }
        ]
      })

      // Step 11: A-Team Briefing
      tour.addStep({
        id: 'ateam-briefing',
        text: createStepContent(
          `Letzter Stopp: Hier trainieren Sie uns auf Ihre Stimme. Je besser wir Sie kennen, desto besser wird Ihr Content.`
        ),
        attachTo: { element: '[data-testid="sidebar-a-team-briefing"]', on: 'right' },
        buttons: [
          { text: '← Zurück', action: tour.back, classes: 'shepherd-button-secondary' },
          { text: 'Weiter →', action: tour.next, classes: 'shepherd-button-primary' }
        ]
      })

      // Step 12: Ende
      tour.addStep({
        id: 'finish',
        text: createStepContent(
          `Das war es! Mein Tipp: Starten Sie mit einem Foto oder fragen Sie mich einfach etwas. Sie finden mich immer hier oben im Cockpit, ob Sie Fragen zur App haben oder Hilfe bei Ihrem Marketing brauchen. 💚`
        ),
        attachTo: { element: '[data-testid="card-lina-briefing"]', on: 'bottom' },
        buttons: [
          { text: 'Tour beenden ✓', action: tour.complete, classes: 'shepherd-button-primary' }
        ]
      })

      tour.on('complete', () => {
        tourActiveRef.current = false
        localStorage.setItem(TOUR_STORAGE_KEY, 'true')
        onTourComplete?.()
      })

      tour.on('cancel', () => {
        tourActiveRef.current = false
        localStorage.setItem(TOUR_STORAGE_KEY, 'true')
      })

      tourRef.current = tour

      setTimeout(() => {
        // Only start tour on desktop/tablet (>= 1024px)
        if (window.innerWidth < 1024) {
          return
        }
        tourActiveRef.current = true
        tour.start()
        onTourStart?.()
      }, 500)
    }

    const timeout = setTimeout(initTour, 500)

    return () => clearTimeout(timeout)
  }, [isFirstTimeUser, onTourComplete, manualTrigger, onTourStart, videoShowing])

  useEffect(() => {
    return () => {
      if (tourRef.current && tourActiveRef.current) {
        tourRef.current.cancel()
      }
      tourRef.current = null
      tourActiveRef.current = false
    }
  }, [])

  return null
}

export function restartDashboardTour() {
  localStorage.removeItem(TOUR_STORAGE_KEY)
  window.location.reload()
}
